<!-- Staff Allocation - matches Project Management dashboard pattern -->
<div class="staff-alloc-shell px-1 py-4 sm:py-6">

  <!-- Header + quick stats -->
  <div class="mb-6">
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 mb-1">Staff allocation</h1>
        <p class="text-gray-900 text-sm font-medium" *ngIf="displayProjectName">{{ displayProjectName }}</p>
        <p class="text-gray-600 text-sm">
          Add staff with start and end dates. Use full or partial allocation; availability is shared across all your projects.
        </p>
      </div>

      <div class="flex flex-wrap gap-3">
        <div class="bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-5.13a4 4 0 11-8 0 4 4 0 018 0zm6 4a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <p class="text-xs text-gray-600">Allocated staff</p>
              <p class="text-lg font-semibold text-gray-900">{{ totalCount }}</p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <svg class="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p class="text-xs text-gray-600">Full</p>
              <p class="text-lg font-semibold text-gray-900">{{ fullCount }}</p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <svg class="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p class="text-xs text-gray-600">Partial</p>
              <p class="text-lg font-semibold text-gray-900">{{ partialCount }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Search + primary action -->
  <div class="bg-white rounded-2xl shadow-md border border-gray-200 p-4 sm:p-5 mb-6">
    <div class="flex flex-col lg:flex-row gap-3 lg:items-center">
      <div class="flex-1">
        <div class="relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search staff by name, code, designation, or remarks..."
            class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl leading-5 bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200"
            [value]="searchKeyword"
            (input)="applyFilter($event)"
          />
        </div>
      </div>

      <div class="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          (click)="clearSearch()"
          *ngIf="searchKeyword"
          class="inline-flex items-center justify-center px-5 py-3 border border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
        >
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M6 18L18 6M6 6l12 12" />
          </svg>
          Clear
        </button>

        <button
          type="button"
          (click)="openAddForm()"
          [disabled]="!projectId || showAddForm"
          class="inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-lg"
        >
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
          Add staff
        </button>
      </div>
    </div>
  </div>

  <!-- Add / edit form card -->
  <div *ngIf="showAddForm" class="bg-white rounded-2xl shadow-md border border-gray-200 mb-6 overflow-hidden">
    <div class="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
      <div>
        <h2 class="text-base font-semibold text-gray-900">{{ formTitle }}</h2>
        <p class="text-xs text-gray-600 mt-0.5" *ngIf="editingAllocationId == null">
          Pick start &amp; end dates first to see who's available.
        </p>
        <p class="text-xs text-gray-600 mt-0.5" *ngIf="editingAllocationId != null">
          Staff cannot be changed when editing.
        </p>
      </div>
      <button
        type="button"
        (click)="cancelAdd()"
        class="inline-flex items-center p-2 rounded-lg text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400"
        aria-label="Close form"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <form [formGroup]="form" (ngSubmit)="onSubmit()" class="p-6 space-y-5">

      <!-- Dates -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">Start date <span class="text-red-500">*</span></label>
          <input
            type="date"
            formControlName="start_date"
            (change)="onDateChange()"
            class="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-colors"
          />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">End date <span class="text-red-500">*</span></label>
          <input
            type="date"
            formControlName="end_date"
            (change)="onDateChange()"
            class="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-colors"
          />
        </div>
      </div>

      <!-- Staff (same record set as the Employees page; staff.isactive=true and not offboarded) -->
      <div>
        <div class="flex items-center justify-between mb-1">
          <label class="block text-xs font-medium text-gray-700">
            Employee / Staff <span class="text-red-500">*</span>
          </label>
          <a
            routerLink="/home/employees"
            target="_blank"
            class="text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            Manage employees ↗
          </a>
        </div>
        <select
          formControlName="staff_id"
          required
          class="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-colors disabled:opacity-70"
        >
          <option [ngValue]="null" disabled>
            <ng-container *ngIf="form.get('start_date')?.value && form.get('end_date')?.value; else pickDates">
              Select an employee
            </ng-container>
            <ng-template #pickDates>Pick start and end date first</ng-template>
          </option>
          <option *ngFor="let s of staffList" [ngValue]="s.id">
            {{ getStaffOptionLabel(s) }}
          </option>
          <option *ngIf="staffList.length === 0 && form.get('start_date')?.value && form.get('end_date')?.value" disabled>
            No active employees in this organization
          </option>
        </select>
        <p
          *ngIf="staffList.length === 0 && form.get('start_date')?.value && form.get('end_date')?.value"
          class="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
        >
          You don't have any active employees yet. In this app "Staff" and "Employees" are the same record —
          add someone on the <a routerLink="/home/employees" class="underline font-medium">Employees</a> page,
          then come back here.
        </p>
      </div>

      <!-- Allocation -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">Allocation</label>
          <select
            formControlName="allocation_type"
            class="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-colors"
          >
            <option value="full">Full (100%)</option>
            <option value="partial">Partial</option>
          </select>
        </div>

        <div *ngIf="form.get('allocation_type')?.value === 'partial'">
          <label class="block text-xs font-medium text-gray-700 mb-1">Percent</label>
          <div class="relative">
            <input
              type="number"
              formControlName="allocation_percent"
              min="1"
              max="99"
              class="block w-full px-3 py-2.5 pr-8 border border-gray-300 rounded-lg bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-colors"
            />
            <span class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
          </div>
        </div>
      </div>

      <!-- Remarks -->
      <div>
        <label class="block text-xs font-medium text-gray-700 mb-1">Remarks <span class="text-gray-400 font-normal">(optional)</span></label>
        <textarea
          formControlName="remarks"
          rows="2"
          class="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-colors resize-y"
        ></textarea>
      </div>

      <div class="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
        <button
          type="button"
          (click)="cancelAdd()"
          class="inline-flex items-center px-5 py-2.5 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          [disabled]="(editingAllocationId == null && form.invalid) || adding"
          class="inline-flex items-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow"
        >
          <svg *ngIf="adding" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          {{ submitLabel }}
        </button>
      </div>
    </form>
  </div>

  <!-- Allocations table card -->
  <div class="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
    <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
      <h2 class="text-base font-semibold text-gray-900">Allocated staff</h2>
      <p class="text-xs text-gray-600 mt-0.5">All people currently assigned to this project, with their allocation window.</p>
    </div>

    <!-- Loading -->
    <div *ngIf="loading" class="flex flex-col items-center justify-center py-16">
      <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-3"></div>
      <p class="text-sm text-gray-600">Loading allocations...</p>
    </div>

    <!-- Table -->
    <div *ngIf="!loading && filteredAllocations.length > 0" class="overflow-x-auto">
      <table class="alloc-table w-full">
        <thead>
          <tr class="bg-gray-50">
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Staff</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start date</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End date</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Allocation</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr *ngFor="let a of filteredAllocations" class="hover:bg-gray-50 transition-colors duration-150">
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="flex items-center gap-3 min-w-0">
                <div class="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold shrink-0">
                  {{ initials(a.full_name) }}
                </div>
                <div class="min-w-0">
                  <div class="text-sm font-medium text-gray-900 truncate" [matTooltip]="a.full_name">{{ a.full_name }}</div>
                  <div class="text-xs text-gray-500 truncate" *ngIf="a.employee_code || a.designation">
                    <span *ngIf="a.employee_code">{{ a.employee_code }}</span>
                    <span *ngIf="a.employee_code && a.designation"> · </span>
                    <span *ngIf="a.designation">{{ a.designation }}</span>
                  </div>
                </div>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              <div class="flex items-center">
                <svg class="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {{ a.start_date | date:'MMM dd, yyyy' }}
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              <div class="flex items-center">
                <svg class="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {{ a.end_date | date:'MMM dd, yyyy' }}
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span
                class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                [ngClass]="{
                  'bg-blue-100 text-blue-800': a.allocation_type === 'full' || a.allocation_percent === 100,
                  'bg-amber-100 text-amber-800': a.allocation_type === 'partial' && a.allocation_percent < 100
                }"
              >
                <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <circle cx="10" cy="10" r="3" />
                </svg>
                {{ allocationLabel(a) }}
              </span>
            </td>
            <td class="px-6 py-4 text-sm text-gray-600 max-w-[16rem]">
              <span *ngIf="a.remarks" [matTooltip]="a.remarks" class="line-clamp-2">{{ a.remarks }}</span>
              <span *ngIf="!a.remarks" class="text-gray-400">—</span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
              <div class="flex items-center justify-end space-x-1">
                <button
                  type="button"
                  (click)="openEdit(a)"
                  class="inline-flex items-center p-2 border border-transparent rounded-lg text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                  matTooltip="Edit allocation"
                  matTooltipPosition="above"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  type="button"
                  (click)="remove(a)"
                  class="inline-flex items-center p-2 border border-transparent rounded-lg text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors duration-200"
                  matTooltip="Remove staff"
                  matTooltipPosition="above"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty: nothing matched search -->
    <div *ngIf="!loading && allocations.length > 0 && filteredAllocations.length === 0" class="text-center py-12">
      <div class="mx-auto w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
      <h3 class="text-base font-medium text-gray-900 mb-1">No matching staff</h3>
      <p class="text-sm text-gray-500 mb-4">Try a different search term.</p>
      <button
        type="button"
        (click)="clearSearch()"
        class="inline-flex items-center px-5 py-2.5 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        Clear search
      </button>
    </div>

    <!-- Empty: no allocations at all -->
    <div *ngIf="!loading && allocations.length === 0" class="text-center py-12">
      <div class="mx-auto w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-5.13a4 4 0 11-8 0 4 4 0 018 0zm6 4a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </div>
      <h3 class="text-base font-medium text-gray-900 mb-1">No staff allocated yet</h3>
      <p class="text-sm text-gray-500 mb-4">Click "Add staff" to assign resources with start/end dates and full or partial allocation.</p>
      <button
        type="button"
        (click)="openAddForm()"
        [disabled]="!projectId"
        class="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
      >
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
        </svg>
        Add staff
      </button>
    </div>
  </div>
</div>
